import { calcComparisonScenario } from '@shared';

import {
  buildCcrcTypeAPolicy,
  computeCcrcPricing,
  type CcrcCoverageType,
} from './ccrc-type-a-pricing';

export interface CcrcTypeAPersonAmount {
  projectedCoverage: number;
  cost: number;
}

export interface CcrcTypeAProductMetrics {
  projectedCoverage: number;
  cost: number;
  totalRoi: number;
  breakdown: { primary: CcrcTypeAPersonAmount; partner: CcrcTypeAPersonAmount } | null;
}

export const EMPTY_CCRC_TYPE_A_METRICS: CcrcTypeAProductMetrics = {
  projectedCoverage: 0,
  cost: 0,
  totalRoi: 0,
  breakdown: null,
};

interface MembershipMetrics {
  projectedCoverage: number;
  cost: number;
  totalRoi: number;
}

const EMPTY_MEMBERSHIP: MembershipMetrics = {
  projectedCoverage: 0,
  cost: 0,
  totalRoi: 0,
};

function computeMembershipMetrics(
  client: Client,
  policy: PolicyFundingSource,
  fundingSourceId: string,
): MembershipMetrics {
  try {
    const scenario = calcComparisonScenario({
      primary: client,
      partner: null,
      includePrimaryExistingPolicies: false,
      injectedPolicies: [{ fundingSourceId, policyFundingSource: policy }],
    });

    const derived = scenario._derivedPrimary.multipleFundingSources?.ltcPolicy?.[fundingSourceId];

    if (!derived) {
      return EMPTY_MEMBERSHIP;
    }

    return {
      projectedCoverage: derived.projectedCostCoverage ?? 0,
      cost: derived.policyTotalCost ?? 0,
      totalRoi: derived.policyProjectedReturnOnInvestment ?? 0,
    };
  } catch {
    return EMPTY_MEMBERSHIP;
  }
}

export function computeCcrcProductMetrics(args: {
  product: CcrcTypeAProduct;
  coverage: CcrcCoverageType;
  client: Client;
  partner: Client | null;
}): CcrcTypeAProductMetrics {
  const { product, coverage, client, partner } = args;

  const pricing = computeCcrcPricing({
    product,
    coverage,
    clientAge: client.intakeSurvey.clientAge,
    partnerAge: partner?.intakeSurvey.clientAge,
    hasPartner: partner != null,
  });

  if (coverage === 'single' || !partner || pricing.partnerEntranceFee == null) {
    const membership = computeMembershipMetrics(
      client,
      buildCcrcTypeAPolicy({
        product,
        entranceFee: pricing.primaryEntranceFee,
        monthlyFee: pricing.monthlyPerPerson,
      }),
      `ccrc-metrics-${product.id}-primary`,
    );

    return {
      projectedCoverage: membership.projectedCoverage,
      cost: membership.cost,
      totalRoi: membership.totalRoi,
      breakdown: null,
    };
  }

  const primary = computeMembershipMetrics(
    client,
    buildCcrcTypeAPolicy({
      product,
      entranceFee: pricing.primaryEntranceFee,
      monthlyFee: pricing.monthlyPerPerson,
    }),
    `ccrc-metrics-${product.id}-primary`,
  );
  const partnerMembership = computeMembershipMetrics(
    partner,
    buildCcrcTypeAPolicy({
      product,
      entranceFee: pricing.partnerEntranceFee,
      monthlyFee: pricing.monthlyPerPerson,
    }),
    `ccrc-metrics-${product.id}-partner`,
  );

  const cost = primary.cost + partnerMembership.cost;
  const totalReturns =
    primary.cost * (1 + primary.totalRoi) +
    partnerMembership.cost * (1 + partnerMembership.totalRoi);

  return {
    projectedCoverage: primary.projectedCoverage + partnerMembership.projectedCoverage,
    cost,
    totalRoi: cost > 0 ? (totalReturns - cost) / cost : 0,
    breakdown: {
      primary: { projectedCoverage: primary.projectedCoverage, cost: primary.cost },
      partner: {
        projectedCoverage: partnerMembership.projectedCoverage,
        cost: partnerMembership.cost,
      },
    },
  };
}
